/**
 * MDJ Ritual — Interactive Sliding Puzzle
 * Maison de Jarin / Kaarnika Foods
 *
 * assets/mdj-shopify.js
 *
 * Bridges the puzzle to the surrounding Shopify theme without assuming
 * anything about that theme's markup. Two integration strategies are
 * used, in order of preference:
 *
 *   1. Native trigger — find the real product form + Add to Cart button
 *      already on the page and submit/click it programmatically. This
 *      hands control back to whatever the theme itself does (AJAX cart
 *      drawer, full page cart, custom cart events) rather than
 *      reimplementing it, so it works correctly regardless of theme.
 *
 *   2. Direct Ajax API fallback — if no compatible form can be found
 *      (e.g. a stripped-down custom theme, or the puzzle is embedded
 *      somewhere without a product form nearby), POST straight to
 *      Shopify's public /cart/add.js endpoint, which exists on every
 *      Shopify store regardless of theme, and dispatch generic cart
 *      events afterward so any listening theme code can react.
 *
 * Public API consumed by mdj-core.js:
 *   addToCart()  -> Promise
 *   buyNow()
 *   destroy()
 */

(function () {
  'use strict';

  window.MDJRitual = window.MDJRitual || {};

  function MDJShopify(rootEl, options) {
    this.root = rootEl;
    this.options = options || {};
  }

  /* ------------------------------------------------------------------ */
  /* Discovery                                                           */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype._findProductForm = function () {
    var node = this.root;

    while (node && node !== document.body) {
      node = node.parentElement;
      if (!node) break;

      var forms = node.querySelectorAll('form[action*="/cart/add"]');

      for (var i = 0; i < forms.length; i++) {
        if (forms[i].querySelector('[name="id"]')) {
          return forms[i];
        }
      }
    }

    var pageForms = document.querySelectorAll('form[action*="/cart/add"]');

    for (var j = 0; j < pageForms.length; j++) {
      if (pageForms[j].querySelector('[name="id"]')) {
        return pageForms[j];
      }
    }

    return null;
  };

  MDJShopify.prototype._findAddToCartButton = function (form) {
    if (!form) return null;

    return (
      form.querySelector('button[name="add"]') ||
      form.querySelector('input[name="add"]') ||
      form.querySelector('button[type="submit"]') ||
      form.querySelector('input[type="submit"]')
    );
  };

  MDJShopify.prototype._getCurrentVariantId = function (form) {
    if (form) {
      var input = form.querySelector('[name="id"]');
      if (input && input.value) return input.value;
    }

    return this.options.variantId || '';
  };

  /* ------------------------------------------------------------------ */
  /* Add to Cart                                                         */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype.addToCart = function () {
    var self = this;

    return new Promise(function (resolve, reject) {

      var form = self._findProductForm();
      var variantId = self._getCurrentVariantId(form);

      if (!variantId) {
        reject(new Error('No variant selected.'));
        return;
      }

      if (form) {
        var button = self._findAddToCartButton(form);

        try {

          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(button || undefined);
          }
          else if (button) {
            button.click();
          }
          else {
            form.submit();
          }

          setTimeout(function () {

            self._refreshCartCount();

            resolve();

          }, 650);

        } catch (err) {

          self._directAjaxAdd(variantId)
            .then(function () {
              self._refreshCartCount();
              resolve();
            })
            .catch(reject);

        }

        return;
      }

      self._directAjaxAdd(variantId)
        .then(function () {
          self._refreshCartCount();
          resolve();
        })
        .catch(reject);

    });
  };

  /* ------------------------------------------------------------------ */
  /* Buy Now                                                             */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype.buyNow = function () {

    var form = this._findProductForm();
    var variantId = this._getCurrentVariantId(form);
    var self = this;

    if (!variantId) {
      window.location.href = this.options.productUrl || '/cart';
      return;
    }

    this._directAjaxAdd(variantId)
      .then(function () {
        window.location.href =
          self._buildPostAddDestination('/checkout');
      })
      .catch(function () {
        window.location.href =
          self.options.productUrl || '/cart';
      });

  };

  MDJShopify.prototype._buildPostAddDestination = function (redirectTo) {

    if (
      this.options.rewardType === 'coupon' &&
      this.options.rewardCoupon
    ) {

      return (
        '/discount/' +
        encodeURIComponent(this.options.rewardCoupon) +
        '?redirect=' +
        encodeURIComponent(redirectTo)
      );

    }

    return redirectTo;

  };

  /* ------------------------------------------------------------------ */
  /* Ajax Add                                                            */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype._directAjaxAdd = function (variantId) {

    var self = this;

    return fetch('/cart/add.js', {

      method: 'POST',

      credentials: 'same-origin',

      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },

      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1
          }
        ]
      })

    })

      .then(function (response) {

        if (!response.ok) {

          return response.json()
            .then(function (json) {

              throw new Error(
                json.description || 'Unable to add product.'
              );

            });

        }

        return response.json();

      })

      .then(function (item) {

        self._dispatchCartEvents(item);

        self._refreshCartCount();

        return item;

      });

  };

  /* ------------------------------------------------------------------ */
  /* Cart Events                                                         */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype._dispatchCartEvents = function (item) {

    var detail = {
      item: item,
      source: 'mdj-ritual'
    };

    document.dispatchEvent(
      new CustomEvent('cart:refresh', { detail: detail })
    );

    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: detail })
    );

    document.dispatchEvent(
      new CustomEvent('cart:build', { detail: detail })
    );

    document.dispatchEvent(
      new CustomEvent('cart:item-added', { detail: detail })
    );

    document.dispatchEvent(
      new CustomEvent('ajaxProduct:added', { detail: detail })
    );

    document.dispatchEvent(
      new CustomEvent('product:added', { detail: detail })
    );

    window.dispatchEvent(
      new Event('cart:refresh')
    );

  };

  /* ------------------------------------------------------------------ */
  /* Refresh Cart Count                                                  */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype._refreshCartCount = function () {

    fetch('/cart.js', {
      credentials: 'same-origin'
    })

      .then(function (response) {
        return response.json();
      })

      .then(function (cart) {

        var selectors = [

          '[data-cart-count]',
          '.cart-count',
          '.header__cart-count',
          '.cart-count-bubble span',
          '.cart-count-bubble',
          '.site-header__cart-count',
          '.header-cart-count'

        ];

        selectors.forEach(function (selector) {

          var nodes = document.querySelectorAll(selector);

          nodes.forEach(function (node) {

            node.textContent = cart.item_count;

            if (cart.item_count > 0) {
              node.classList.remove('hidden');
              node.removeAttribute('hidden');
            }

          });

        });

      })

      .catch(function (error) {
        console.warn('Cart count refresh failed:', error);
      });

  };

  /* ------------------------------------------------------------------ */
  /* Destroy                                                             */
  /* ------------------------------------------------------------------ */

  MDJShopify.prototype.destroy = function () {};

  window.MDJRitual.Shopify = MDJShopify;

})();